import type { SpecContextFocusChip, SpecStoredConversationContext } from './spec-context.js';
import type { SpecContextRecordType, SpecContextScopeDataset } from './spec.js';

/**
 * Per-conversation memory for the context chip and the connector off-list.
 * Pure and dependency-free; the follow panel and the full page read the same
 * key, so they agree.
 */

export type SpecContextStorage = Pick<Storage, 'getItem' | 'setItem'>;

const KEY_PREFIX = 'parcel:spec-context';
const SCOPES = new Set<string>(['account', 'contact', 'project']);
const RECORD_TYPES = new Set<string>(['account', 'contact', 'project', 'view']);

export function defaultSpecContextStorage(): SpecContextStorage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function specContextStorageKey(
  workspaceId: string | null | undefined,
  conversationId: string | null | undefined,
): string | null {
  if (!workspaceId || !conversationId) return null;
  return `${KEY_PREFIX}:${workspaceId}:${conversationId}`;
}

function asChip(value: unknown): SpecContextFocusChip | null {
  if (typeof value !== 'object' || value === null) return null;
  const chip = value as { focus?: unknown; label?: unknown };
  const focus = chip.focus as
    | { kind?: unknown; dataset?: unknown; type?: unknown; id?: unknown }
    | undefined;
  const label = typeof chip.label === 'string' ? chip.label : null;
  if (focus?.kind === 'scope' && typeof focus.dataset === 'string' && SCOPES.has(focus.dataset)) {
    return {
      focus: { kind: 'scope', dataset: focus.dataset as SpecContextScopeDataset },
      label,
    };
  }
  if (
    focus?.kind === 'record' &&
    typeof focus.type === 'string' &&
    RECORD_TYPES.has(focus.type) &&
    typeof focus.id === 'string' &&
    focus.id.length > 0
  ) {
    return {
      focus: { kind: 'record', type: focus.type as SpecContextRecordType, id: focus.id },
      label,
    };
  }
  return null;
}

export function readSpecConversationContext(
  storage: SpecContextStorage,
  key: string,
): SpecStoredConversationContext | null {
  let raw: string | null;
  try {
    raw = storage.getItem(key);
  } catch {
    return null;
  }
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null) return null;
  const record = parsed as { focus?: unknown; connectionsOff?: unknown; webOff?: unknown };
  const connectionsOff = Array.isArray(record.connectionsOff)
    ? record.connectionsOff.filter((id): id is string => typeof id === 'string' && id.length > 0)
    : [];
  return { focus: asChip(record.focus), connectionsOff, webOff: record.webOff === true };
}

export function writeSpecConversationContext(
  storage: SpecContextStorage,
  key: string,
  value: SpecStoredConversationContext,
): void {
  try {
    storage.setItem(
      key,
      JSON.stringify({
        focus: value.focus,
        connectionsOff: value.connectionsOff,
        ...(value.webOff ? { webOff: true } : {}),
      }),
    );
  } catch {
    // Storage is a convenience; a full or locked store must not break a send.
  }
}
